/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Step validation helpers for PaymentModal registration and payment flow.
 * @module utils/payment/paymentStepValidation
 */

import { validateRFC } from "../common.js";
import {
  detectCardType,
  validateExpiryDate,
} from "./cardValidation.js";

/**
 * Validates invoice confirmation phone when the user requests an invoice.
 * @param {Object} options
 * @param {"cash"|"card"} options.method
 * @param {boolean} options.wantsInvoiceCash
 * @param {boolean} options.wantsInvoiceCard
 * @param {string} options.phoneCountryCodeCash
 * @param {string} options.phoneCountryCodeCard
 * @param {string} options.confirmPhoneCash
 * @param {string} options.confirmPhoneCard
 * @param {Function} options.getErrorMessage
 * @returns {boolean}
 */
export const isInvoicePhoneValid = ({
  method,
  wantsInvoiceCash,
  wantsInvoiceCard,
  phoneCountryCodeCash,
  phoneCountryCodeCard,
  confirmPhoneCash,
  confirmPhoneCard,
  getErrorMessage,
}) => {
  const isCash = method === "cash";
  const wantsInvoice = isCash ? wantsInvoiceCash : wantsInvoiceCard;
  const countryCode = isCash ? phoneCountryCodeCash : phoneCountryCodeCard;
  const phone = isCash ? confirmPhoneCash : confirmPhoneCard;
  const validationField = isCash ? "confirmPhoneCash" : "confirmPhoneCard";

  if (!wantsInvoice) {
    return true;
  }

  if (!countryCode?.trim() || !phone?.trim()) {
    return false;
  }

  const digitsOnly = phone.replace(/\s/g, "");
  const isValidPhone = /^\d{10}$/.test(digitsOnly);
  const hasPhoneValidationError = Boolean(getErrorMessage(validationField));

  return isValidPhone && !hasPhoneValidationError;
};

/**
 * Validates step 1 (company and personal registration fields).
 * @param {Object} options
 * @param {Object} options.formData
 * @param {Object} options.event
 * @param {boolean} options.hasErrors - From useFieldValidation
 * @returns {boolean}
 */
export const isPaymentStep1Valid = ({ formData, event, hasErrors }) => {
  const {
    razonSocial,
    RFC,
    sector,
    nombre,
    telefono,
    correoElectronico,
    interesesComerciales,
    expectativas,
  } = formData;

  const trimmedRfc = RFC?.trim() ?? "";
  const rfcValidation = trimmedRfc
    ? validateRFC(trimmedRfc)
    : { isValid: false };

  const requiredFieldsValid =
    razonSocial?.trim() !== "" &&
    trimmedRfc !== "" &&
    rfcValidation.isValid &&
    sector &&
    nombre?.trim() !== "" &&
    telefono?.trim() !== "" &&
    correoElectronico?.trim() !== "";

  const conditionalFieldsValid =
    (!event?.interesesComercialesRequerido ||
      interesesComerciales?.trim() !== "") &&
    (!event?.expectativasRequerido || expectativas?.trim() !== "");

  return requiredFieldsValid && conditionalFieldsValid && !hasErrors;
};

/**
 * Validates step 2 (payment method selection and card or invoice phone fields).
 * @param {Object} options
 * @param {string|null} options.paymentMethod
 * @param {Object} options.formData
 * @param {Object} options.invoicePhoneValidationArgs - Args for {@link isInvoicePhoneValid}
 * @returns {boolean}
 */
export const isPaymentStep2Valid = ({
  paymentMethod,
  formData,
  invoicePhoneValidationArgs,
}) => {
  if (!paymentMethod) {
    return false;
  }

  if (paymentMethod === "cash") {
    return isInvoicePhoneValid({
      ...invoicePhoneValidationArgs,
      method: "cash",
    });
  }

  if (paymentMethod === "card") {
    const {
      nombreTitular,
      numeroTarjeta,
      fechaDeVencimiento,
      codigoDeSeguridad,
    } = formData;

    if (
      !numeroTarjeta ||
      !fechaDeVencimiento ||
      !codigoDeSeguridad ||
      !nombreTitular
    ) {
      return false;
    }

    const cardNumber = numeroTarjeta.replace(/\s/g, "");
    const detectedCardType = detectCardType(cardNumber);
    const expiryValidation = validateExpiryDate(fechaDeVencimiento);
    const isValidCardNumber =
      cardNumber.length >= 13 && detectedCardType !== "";

    return (
      isValidCardNumber &&
      nombreTitular.trim() !== "" &&
      fechaDeVencimiento.trim() !== "" &&
      expiryValidation.isValid &&
      codigoDeSeguridad.length >= 3 &&
      isInvoicePhoneValid({
        ...invoicePhoneValidationArgs,
        method: "card",
      })
    );
  }

  return false;
};

/** Step 3 has no additional client-side validation before payment submission. */
export const isPaymentStep3Valid = () => true;
